import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  User as FirebaseUser,
  signInWithRedirect,
  getRedirectResult,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  getIdToken,
  IdTokenResult,
  AuthError
} from 'firebase/auth';
import {
  auth,
  googleProvider,
  githubProvider,
  appleProvider,
  setPersistence,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword
} from '../config/firebase';
import { apiService } from '../services/api';
import { logger } from '../services/logger/BrowserLogger';
import { attributionService } from '../services/attributionService';
import { getFunctionUrl } from '../config/v2FunctionUrls';

export type ProgrammingLanguage = 'python' | 'typescript' | 'java' | 'golang';
export type UserRole = 'free' | 'premium' | 'admin';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  emailVerified: boolean;
  preferredLanguage: ProgrammingLanguage;
  role: UserRole;
  customClaims?: Record<string, any>;
  createdAt: Date;
  lastLoginAt: Date;
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signInWithGoogle: (rememberMe?: boolean) => Promise<void>;
  signInWithGithub: (rememberMe?: boolean) => Promise<void>;
  signInWithApple: (rememberMe?: boolean) => Promise<void>;
  signInWithEmail: (email: string, password?: string) => Promise<void>;
  signOut: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshToken: () => Promise<void>;
  checkCustomClaims: () => Promise<IdTokenResult | null>;
  updatePreferredLanguage: (language: ProgrammingLanguage) => Promise<void>;
  error: string | null;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const FirebaseAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use ref to ensure we only check redirect result ONCE per app lifecycle
  const redirectCheckInitiated = React.useRef(false);
  // Track if redirect check is still in progress
  const redirectCheckInProgress = React.useRef(false);
  // Track if attribution has been sent for current user
  const attributionSent = React.useRef(false);

  const applyAuthenticatedUser = useCallback(async (firebaseUser: FirebaseUser) => {
    // SET CURRENT USER IMMEDIATELY - before any async operations that might fail
    // This ensures isAuthenticated becomes true even if profile loading fails
    setCurrentUser(firebaseUser);

    try {
      const tokenResult = await firebaseUser.getIdTokenResult();
      const profileResponse = await apiService.getUserProfile();

      if (profileResponse) {
        setUserProfile({
          uid: firebaseUser.uid,
          email: firebaseUser.email!,
          displayName: firebaseUser.displayName || '',
          photoURL: firebaseUser.photoURL || '',
          emailVerified: firebaseUser.emailVerified,
          preferredLanguage: profileResponse.preferredLanguage || 'python',
          role: (tokenResult.claims.role as UserRole) || 'free',
          customClaims: tokenResult.claims,
          createdAt: profileResponse.createdAt,
          lastLoginAt: new Date(),
        });
      }
    } catch (profileError) {
      logger.error('Failed to load user profile', profileError as Error);

      // Even if profile fetch fails, create a fallback profile
      // User is still authenticated, just with minimal info
      setUserProfile({
        uid: firebaseUser.uid,
        email: firebaseUser.email!,
        displayName: firebaseUser.displayName || '',
        photoURL: firebaseUser.photoURL || '',
        emailVerified: firebaseUser.emailVerified,
        preferredLanguage: 'python',
        role: 'free',
        customClaims: {},
        createdAt: new Date(),
        lastLoginAt: new Date(),
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Process redirect result on app initialization
  useEffect(() => {
    const checkRedirectResult = async () => {
      // Prevent multiple calls to getRedirectResult
      if (redirectCheckInitiated.current) {
        return;
      }

      redirectCheckInitiated.current = true;
      redirectCheckInProgress.current = true;  // Mark as in progress

      try {
        // CRITICAL FIX: Wait for Firebase Auth to initialize before checking redirect
        logger.info('checkRedirectResult: Waiting for auth.authStateReady()...');
        await auth.authStateReady();
        logger.info('checkRedirectResult: auth.authStateReady() complete', {
          hasCurrentUser: !!auth.currentUser,
          hasEmail: !!auth.currentUser?.email
        });

        logger.info('checkRedirectResult: Calling getRedirectResult()...');
        const result = await getRedirectResult(auth);
        logger.info('checkRedirectResult: getRedirectResult() returned', {
          hasResult: !!result,
          hasUser: !!result?.user,
          providerId: result?.providerId
        });

        if (result && result.user) {
          logger.info('OAuth redirect successful', {
            uid: result.user.uid,
            email: result.user.email,
            provider: result.providerId
          });

          await applyAuthenticatedUser(result.user);

          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('auth-redirect-complete', {
                detail: { user: result.user, result }
              })
            );
          }, 50);
        } else {
          // No redirect result - could be normal page load or redirect failed silently
          logger.info('checkRedirectResult: No redirect result', {
            hasCurrentUser: !!auth.currentUser,
            hasEmail: !!auth.currentUser?.email
          });
          if (!auth.currentUser) {
            // No redirect result and no current user - set loading to false so UI can render
            setIsLoading(false);
          }
        }
      } catch (error) {
        logger.error('Failed to process redirect result', error as Error, {
          errorCode: (error as any)?.code,
          errorMessage: (error as any)?.message
        });
        // On error, set loading to false so UI can still render
        setIsLoading(false);
      } finally {
        // Mark redirect check as complete
        redirectCheckInProgress.current = false;
      }
    };

    checkRedirectResult();
  }, [applyAuthenticatedUser]);

  // Listen to Firebase Auth state changes
  useEffect(() => {

    const unsubscribe = onAuthStateChanged(
      auth,
      async firebaseUser => {
        if (firebaseUser) {
          await applyAuthenticatedUser(firebaseUser);
        } else {
          // Don't clear state if we're still checking for redirect result
          if (redirectCheckInProgress.current) {
            return;
          }

          setCurrentUser(null);
          setUserProfile(null);
          setIsLoading(false);
        }
      },
      error => {
        logger.error('Auth state error', error);
        setError(error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [applyAuthenticatedUser]);

  // Send attribution to backend when user authenticates
  useEffect(() => {
    const sendAttribution = async () => {
      if (currentUser && !attributionSent.current) {
        const source = attributionService.getStoredSource();
        if (source) {
          try {
            // Get Firebase ID token for authentication
            const token = await currentUser.getIdToken();

            // Send attribution to backend with auth token
            const response = await fetch(getFunctionUrl('setUserAttribution'), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ source }), // userId extracted from token on server
            });

            if (response.ok) {
              attributionService.clearStoredSource();
              attributionSent.current = true;
              logger.info('Attribution sent for user', { source });
            }
          } catch (err) {
            // Silent fail - will retry on next session or if currentUser changes
            logger.warn('Failed to send attribution', err);
          }
        }
      }
    };

    sendAttribution();
  }, [currentUser]);

  const signInWithGoogle = async (rememberMe: boolean = false): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);

      logger.info('Starting Google sign-in redirect...', { rememberMe });

      // Set persistence before sign in
      await setPersistence(rememberMe);

      // Sign in with Google (redirect flow)
      logger.info('Calling signInWithRedirect for Google...');
      await signInWithRedirect(auth, googleProvider);

      // User redirects to OAuth provider. When they return, checkRedirectResult()
      // in this component will process the result via getRedirectResult()

    } catch (error: any) {
      const authError = error as AuthError;
      logger.error('Google sign-in redirect failed', authError);
      setError(authError.message || 'Sign-in failed');
      setIsLoading(false);
      throw error;
    }
    // Note: setIsLoading(false) only in catch because redirect prevents finally from running
  };

  const signInWithGithub = async (rememberMe: boolean = false): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);

      // Set persistence before sign in
      await setPersistence(rememberMe);

      // Sign in with GitHub (redirect flow)
      await signInWithRedirect(auth, githubProvider);

      // User redirects to OAuth provider. When they return, checkRedirectResult()
      // in this component will process the result via getRedirectResult()

    } catch (error: any) {
      const authError = error as AuthError;
      logger.error('GitHub sign-in redirect failed', authError);
      setError(authError.message || 'Sign-in failed');
      setIsLoading(false);
      throw error;
    }
    // Note: setIsLoading(false) only in catch because redirect prevents finally from running
  };

  const signInWithApple = async (rememberMe: boolean = false): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);

      // Set persistence before sign in
      await setPersistence(rememberMe);

      // Sign in with Apple (redirect flow)
      await signInWithRedirect(auth, appleProvider);

      // User redirects to OAuth provider. When they return, checkRedirectResult()
      // in this component will process the result via getRedirectResult()

    } catch (error: any) {
      const authError = error as AuthError;
      logger.error('Apple sign-in redirect failed', authError);
      setError(authError.message || 'Sign-in failed');
      setIsLoading(false);
      throw error;
    }
    // Note: setIsLoading(false) only in catch because redirect prevents finally from running
  };

  const signInWithEmail = async (email: string, password: string = 'dev-password-123'): Promise<void> => {
    try {
      setError(null);
      setIsLoading(true);

      logger.info('Attempting email/password sign-in for emulator', { email });

      try {
        // Try to sign in with existing user
        await signInWithEmailAndPassword(auth, email, password);
        logger.info('Signed in with existing user', { email });
      } catch (error: any) {
        // If user doesn't exist, create a new one
        if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
          logger.info('User not found, creating new user', { email });
          await createUserWithEmailAndPassword(auth, email, password);
          logger.info('Created and signed in new user', { email });
        } else {
          throw error;
        }
      }

      // Backend will automatically create/update user document
      // when the first authenticated request is made

    } catch (error: any) {
      const authError = error as AuthError;
      logger.error('Email/password sign-in failed', authError);
      setError(authError.message || 'Sign-in failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try {
      setIsLoading(true);
      await firebaseSignOut(auth);
      setUserProfile(null);
      logger.info('User signed out');
    } catch (error) {
      logger.error('Sign-out failed', error as Error);
      setError('Failed to sign out');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const getAccessToken = async (): Promise<string | null> => {
    if (!currentUser) return null;

    try {
      const token = await getIdToken(currentUser, /* forceRefresh */ false);
      return token;
    } catch (error) {
      logger.error('Failed to get access token', error as Error);
      return null;
    }
  };

  const refreshToken = async (): Promise<void> => {
    if (!currentUser) return;

    try {
      await getIdToken(currentUser, /* forceRefresh */ true);
      logger.debug('Token refreshed');
    } catch (error) {
      logger.error('Failed to refresh token', error as Error);
      throw error;
    }
  };

  const checkCustomClaims = async (): Promise<IdTokenResult | null> => {
    if (!currentUser) return null;

    try {
      const tokenResult = await currentUser.getIdTokenResult();
      return tokenResult;
    } catch (error) {
      logger.error('Failed to get custom claims', error as Error);
      return null;
    }
  };

  const updatePreferredLanguage = async (language: ProgrammingLanguage): Promise<void> => {
    try {
      await apiService.updateUserPreferences(language);
      setUserProfile(prev => prev ? { ...prev, preferredLanguage: language } : null);
      logger.info('Preferred language updated', { language });
    } catch (error) {
      logger.error('Failed to update preferred language', error as Error);
      throw error;
    }
  };

  const clearError = () => setError(null);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    isLoading,
    isAuthenticated: !!currentUser,
    signInWithGoogle,
    signInWithGithub,
    signInWithApple,
    signInWithEmail,
    signOut,
    getAccessToken,
    refreshToken,
    checkCustomClaims,
    updatePreferredLanguage,
    error,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
